/* WP-10 tiles (§14.4.6): BrandGroupHeader, BoxTile (selectable, role
 * checkbox), ShopChoice (radio), DateChip (radiogroup). */
import type { ReactNode } from 'react';
import { CheckIcon } from '../icons';
import type { PortalBox } from '../api/types';
import { mediaUrl, productBoxPicture, useAppearance } from '../appearance/appearance';
import { Box10Illustration, Box25Illustration, Box50Illustration } from '../illustrations';
import { cn } from '../utils/cn';

export function BrandGroupHeader({
  mark,
  name,
  countLabel,
}: {
  mark?: ReactNode;
  name: ReactNode;
  countLabel?: ReactNode;
}) {
  return (
    <div className="hp-brand__head">
      <span className="hp-brand__mark" aria-hidden>
        {mark ?? <span>{typeof name === 'string' ? name.slice(0, 1) : '·'}</span>}
      </span>
      <h2 className="hp-brand__name">{name}</h2>
      {countLabel != null ? <span className="hp-brand__count">{countLabel}</span> : null}
    </div>
  );
}

export type BoxTileState = 'available' | 'selected' | 'inCollection' | 'onHold' | 'inTransit';

export function BoxTile({
  box,
  selected,
  onToggle,
  onInfo,
  flag,
  statusLabel,
  ariaLabel,
}: {
  box: PortalBox;
  selected: boolean;
  onToggle?: () => void;
  onInfo?: () => void;
  flag?: { icon?: ReactNode; text: string } | null;
  statusLabel: string;
  ariaLabel?: string;
}) {
  useAppearance(); // re-render when the published bundle lands/changes
  const locked = box.status !== 'AVAILABLE';
  const state: BoxTileState = selected
    ? 'selected'
    : box.status === 'IN_COLLECTION'
      ? 'inCollection'
      : box.status === 'ON_HOLD'
        ? 'onHold'
        : box.status === 'IN_TRANSIT'
          ? 'inTransit'
          : 'available';
  const picture = mediaUrl(box.pictureAssetId) ?? productBoxPicture(box.productId);
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={selected}
      aria-label={ariaLabel}
      className={cn(
        'hp-tile',
        state === 'selected' && 'hp-tile--selected',
        locked && 'hp-tile--locked',
      )}
      onClick={locked ? onInfo : onToggle}
      data-box-ref={box.boxRef}
    >
      <span className="hp-tile__art" aria-hidden>
        {picture ? (
          <img src={picture} alt="" loading="lazy" />
        ) : (
          <BoxFallback sticks={box.sticksInBox} title={box.productName} />
        )}
      </span>
      <span className="hp-tile__year hp-num">{box.boxYear ?? '—'}</span>
      <span className="hp-tile__name">{box.productName}</span>
      <span className="hp-tile__meta">{flag ? flag.text : statusLabel}</span>
      {flag?.icon ? (
        <span className="hp-tile__flag">
          {flag.icon}
          {flag.text}
        </span>
      ) : null}
      <span className="hp-tile__check" aria-hidden>
        <CheckIcon size={14} />
      </span>
    </button>
  );
}

/* R5 task-first — compact, scannable collection row (UX25-06). Same
 * selection contract as BoxTile: role=checkbox, aria-checked, data-box-ref,
 * --locked opens the info sheet instead of toggling. */
export function BoxRow({
  box,
  selected,
  onToggle,
  onInfo,
  statusLabel,
  ariaLabel,
  refLabel,
}: {
  box: PortalBox;
  selected: boolean;
  onToggle?: () => void;
  onInfo?: () => void;
  statusLabel: string;
  ariaLabel?: string;
  refLabel?: string;
}) {
  const locked = box.status !== 'AVAILABLE';
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={selected}
      aria-label={ariaLabel}
      className={cn('hp-boxrow', selected && 'hp-boxrow--selected', locked && 'hp-boxrow--locked')}
      onClick={locked ? onInfo : onToggle}
      data-box-ref={box.boxRef}
    >
      <span className="hp-boxrow__check" aria-hidden>
        <CheckIcon size={14} />
      </span>
      <span className="hp-boxrow__body">
        <span className="hp-boxrow__name">{box.productName}</span>
        <span className="hp-boxrow__meta">
          {box.packLabel} · {box.boxYear ?? '—'}
          {refLabel ? ` · ${refLabel}` : ''}
        </span>
      </span>
      <span className="hp-boxrow__status">{statusLabel}</span>
    </button>
  );
}

export function ShopChoice({
  name,
  address,
  hours,
  thumbnail,
  selected,
  unavailable,
  unavailableLabel,
  onSelect,
}: {
  name: string;
  address?: string | null;
  hours?: string | null;
  thumbnail?: ReactNode;
  selected: boolean;
  unavailable?: boolean;
  unavailableLabel?: string;
  onSelect?: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      className={cn(
        'hp-shop',
        selected && 'hp-shop--selected',
        unavailable && 'hp-shop--unavailable',
      )}
      onClick={unavailable ? undefined : onSelect}
      aria-disabled={unavailable || undefined}
    >
      <span className="hp-shop__thumb" aria-hidden>
        {thumbnail}
      </span>
      <span className="hp-shop__body">
        <span className="hp-shop__name">{name}</span>
        {address ? <span className="hp-shop__addr">{address}</span> : null}
        {unavailable && unavailableLabel ? (
          <span className="hp-shop__addr">{unavailableLabel}</span>
        ) : hours ? (
          <span className="hp-shop__addr">{hours}</span>
        ) : null}
      </span>
      <span className="hp-shop__radio" aria-hidden />
    </button>
  );
}

export function DateChip({
  date,
  dowLabel,
  dayNum,
  selected,
  unavailable,
  unavailableReason,
  onSelect,
}: {
  date: string;
  dowLabel: string;
  dayNum: number;
  selected: boolean;
  unavailable?: boolean;
  unavailableReason?: string;
  onSelect?: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-description={unavailable ? unavailableReason : undefined}
      className={cn(
        'hp-datechip',
        selected && 'hp-datechip--selected',
        unavailable && 'hp-datechip--off',
      )}
      onClick={unavailable ? undefined : onSelect}
      aria-disabled={unavailable || undefined}
      tabIndex={unavailable ? -1 : undefined}
      data-date={date}
    >
      <span className="hp-datechip__dow">{dowLabel}</span>
      <span className="hp-datechip__day hp-num">{dayNum}</span>
    </button>
  );
}

export function Stepper({
  value,
  max,
  onChange,
  minusLabel = '−',
  plusLabel = '+',
}: {
  value: number;
  max: number;
  onChange: (next: number) => void;
  minusLabel?: string;
  plusLabel?: string;
}) {
  return (
    <span className="hp-row" role="group">
      <button
        type="button"
        className="hp-btn hp-btn--secondary"
        style={{ minWidth: 44 }}
        onClick={() => onChange(Math.max(0, value - 1))}
        aria-label={minusLabel}
      >
        −
      </button>
      <span className="hp-num" aria-live="polite">
        {value}
      </span>
      <button
        type="button"
        className="hp-btn hp-btn--secondary"
        style={{ minWidth: 44 }}
        onClick={() => onChange(Math.min(max, value + 1))}
        aria-label={plusLabel}
        disabled={value >= max}
      >
        +
      </button>
    </span>
  );
}

function BoxFallback({ sticks, title }: { sticks: number; title: string }) {
  if (sticks <= 10) return <Box10Illustration title={title} />;
  if (sticks <= 25) return <Box25Illustration title={title} />;
  return <Box50Illustration title={title} />;
}
